import React, { useEffect, useState } from "react";
import AgentsCrumb from "./AgentsCrumb";
import { observer } from "mobx-react";
import { useHistory, useParams } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import AgentPageSkeleton from "./AgentPageSkeleton";
import { AgentOrb, progClass, timeAgo, visibleSteps, runWhen } from "./AgentCard";

// Decision #16: poll the run every 3 s, only while this screen is open and the run is moving.
// Runs saved before notes were rewritten still say "Step — would run on 25 records"; read them plainly.
const plainNote = n => { const t = String(n).replace(/^[^\u2014]*\u2014\s*(would run|won't run)/, "$1").replace(/\s+\u2014\s+/g, ": ").replace(/\brecords?\b/g, m => (m === "record" ? "contact" : "contacts")).replace(/^preview: would source/, "would find").replace(/^no prior memory; kept (\d+)/, "first run, nobody to skip yet ($1 kept)"); return t.charAt(0).toUpperCase() + t.slice(1); };

const POLL_MS = 3000;
const ACTIVE = ["QUEUED", "RUNNING"];
const DONE = ["ENROLLED", "PROCESSED", "WOULD_ENROLL", "WOULD_PROCESS"];

const KIND = { source: "Source", expand: "Expand", enrich: "Enrich", filter: "Filter", branch: "Branch",
  wait: "Wait", act: "Action", notify: "Notify" };

const OUTCOME = {
  IN_PROGRESS: { label: "Working…", cls: "is-live" },
  ENROLLED: { label: "Enrolled", cls: "is-good" },
  PROCESSED: { label: "Done", cls: "is-good" },
  WOULD_ENROLL: { label: "Would enroll", cls: "is-good" },
  WOULD_PROCESS: { label: "Would process", cls: "is-good" },
  SKIPPED: { label: "Skipped", cls: "is-skip" },
  STOPPED: { label: "Stopped", cls: "is-skip" },
  AWAITING_APPROVAL: { label: "Awaiting approval", cls: "is-wait" },
  WAITING: { label: "Waiting", cls: "is-wait" },
  REJECTED: { label: "Not approved", cls: "is-skip" }
};

const TITLE = {
  SUCCEEDED: "Run finished", PARTIAL: "Run finished with some misses", FAILED: "Run failed",
  CANCELLED: "Run stopped", WAITING_APPROVAL: "Waiting for your approval", SLEEPING: "Run is waiting"
};

const startedBy = src => {
  if (src === "schedule.recurring") return "by its schedule";
  if (src && src.startsWith("event.")) return "by an event";
  return "by you";
};

const duration = (a, b) => {
  if (!a) return "";
  const s = Math.max(0, Math.round(((b ? new Date(b) : new Date()) - new Date(a)) / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

const initials = name => String(name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("");

/** "Watch live": one run of an agent, updating while it works, then its summary once it's done. */
const AgentRunLive = observer(() => {
  const { id, runId } = useParams();
  const history = useHistory();
  const [stepDefs, setStepDefs] = useState(agentsData.stepTypesCache || []);
  const [filter, setFilter] = useState("ALL");
  const [openRow, setOpenRow] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    agentsData.clearLiveRun();
    if (!agentsData.seedAgent(id)) agentsData.getAgent(id);
    agentsData.fetchStepTypes().then(setStepDefs);
    let alive = true;
    let timer = null;
    let first = true;
    const tick = async () => {
      // Always load once; after that, skip polls while the tab is in the background.
      if (first || !document.hidden) {
        first = false;
        const run = await agentsData.fetchRun(id, runId);
        if (!alive) return;
        if (run === null) { setMissing(true); return; } // the server says it isn't there
        if (run) { // undefined = a failed poll (502, timeout): keep polling
          agentsData.fetchRunRecords(id, runId);
          if (!ACTIVE.includes(run.status)) return; // settled: stop polling
        }
      }
      timer = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, [id, runId]);

  const agent = agentsData.currentAgent;
  const run = agentsData.liveRun;
  if (missing) {
    return (
      <div className="ag-page">
        <div className="ag-empty">
          <div className="ag-empty__title">Run not found</div>
          <div className="ag-empty__actions">
            <button className="ag-btn ag-btn--primary" onClick={() => history.push(`/agents/${id}`)}><i className="material-icons-outlined">arrow_back</i>Back to the agent</button>
          </div>
        </div>
      </div>
    );
  }
  if (!agent || !run || String(run.id) !== String(runId)) return <AgentPageSkeleton />;

  const live = ACTIVE.includes(run.status);
  const records = agentsData.runRecords[runId] || [];
  const stepName = type => {
    const d = (stepDefs || []).find(x => x.type === type);
    return (d && d.displayName) || String(type || "").split(".").pop().replace(/_/g, " ");
  };
  const steps = visibleSteps(agent);
  const summaries = run.steps || [];
  const summaryOf = n => summaries.find(s => s.nodeId === n.nodeId);
  const doneCount = steps.filter(n => summaryOf(n)).length;
  const inStep = run.stepProgress && run.stepProgress.total ? run.stepProgress : null;

  const inProgress = records.filter(r => r.disposition === "IN_PROGRESS").length;
  const skipped = records.filter(r => r.disposition === "SKIPPED").length;
  const cap = agent.config && agent.config.creditBudgetPerRun;
  const enrolls = (agent.flow || []).some(n => n.stepType === "act.enroll_sequence");
  const outbound = steps.find(n => String(n.stepType).startsWith("act."));

  const FILTERS = [
    { key: "ALL", label: "All", n: records.length },
    { key: "DONE", label: live ? "Handled" : "Done", n: records.filter(r => DONE.includes(r.disposition)).length },
    { key: "SKIPPED", label: "Skipped", n: skipped },
    { key: "IN_PROGRESS", label: "In progress", n: inProgress }
  ].filter(f => f.key === "ALL" || f.n > 0);
  const shown = records
    .filter(r => filter === "ALL"
      || (filter === "DONE" ? DONE.includes(r.disposition) : r.disposition === filter))
    .slice()
    .reverse(); // newest first

  const stop = () => {
    confirmationPopupState.setPopupValues({
      content: "Stop this run? It stops at the next contact or step, whichever comes first. Contacts already enrolled stay enrolled, and credits already used stay used.",
      actionBtnText: "Stop run", cancelBtnText: "Keep running", needCancelBtn: true,
      callback: () => agentsData.stopRun(id, runId)
    });
    confirmationPopupState.setShowPopup(true);
  };

  return (
    <div className="ag-detail">
      <div className="ag-detail__scroll">
        <div className="ag-live">
          <AgentsCrumb items={[{ label: agent.name, to: `/agents/${id}` }, { label: run.dryRun ? "Test run" : "Run" }]} />

          <div className="ag-live__head">
            <AgentOrb mode={live ? "working" : run.status === "WAITING_APPROVAL" ? "waiting" : run.status === "FAILED" ? "failing" : "rest"} size="lg" />
            <div className="ag-live__who">
              <div className="ag-live__titlerow">
                <h1 className="ag-live__title">{live ? agent.name : (TITLE[run.status] || "Run")}</h1>
                {live && <span className="ag-live__badge"><span className="ag-livedot" />{run.cancelRequested ? "STOPPING" : run.status}</span>}
                {!live && run.endedAt && <span className="ag-live__took">Took {duration(run.startedAt, run.endedAt)}</span>}
              </div>
              <span className="ag-live__sub">
                {live
                  ? `Started ${timeAgo(run.startedAt)} ${startedBy(run.triggerSource)} · updates every few seconds`
                  : `${agent.name} · started ${runWhen(run.startedAt).replace(/^./, ch => ch.toLowerCase())} ${startedBy(run.triggerSource)}`}
                {run.dryRun ? " · test run, nothing was changed" : ""}
              </span>
            </div>
            {live && (
              <div className="ag-live__counters">
                {inStep
                  ? <div><b>{inStep.done} <small>of {inStep.total}</small></b><span>done in this step</span></div>
                  : <div><b>{records.length}</b><span>contacts in this run</span></div>}
                <span className="ag-live__vsep" />
                <div><b>{run.creditsUsed || 0}{cap ? <small> of {cap} cap</small> : null}</b><span>credits used</span></div>
              </div>
            )}
            {live && (
              <button className="ag-btn" disabled={run.cancelRequested} onClick={stop}><i className="material-icons-outlined">stop</i>
                {run.cancelRequested ? "Stopping…" : "Stop run…"}
              </button>
            )}
          </div>

          {!live && (
            <div className="ag-live__tiles">
              <div className="ag-live__tile"><span>Processed</span><b>{run.inputCount || records.length}</b><small>found by the run</small></div>
              <div className="ag-live__tile">
                <span>{run.dryRun ? (enrolls ? "Would enroll" : "Would process") : enrolls ? "Enrolled" : "Done"}</span>
                <b className="is-good">{run.enrolledCount}</b>
                <small>{run.dryRun ? "nothing was changed" : enrolls ? "added to the sequence" : "handled end to end"}</small>
              </div>
              <div className="ag-live__tile"><span>Skipped</span><b>{run.skippedCount}</b><small>with a reason on each</small></div>
              <div className="ag-live__tile">
                <span>{run.dryRun ? "Est. credits" : "Credits used"}</span><b>{run.creditsUsed}</b>
                <small>{run.dryRun ? "a real run would use about this" : cap ? `of a ${cap} credit cap` : "this run"}</small>
              </div>
            </div>
          )}

          {run.status === "WAITING_APPROVAL" && (
            <div className="ag-live__callout">
              <AgentOrb mode="waiting" size="sm" />
              <div>
                <b>{records.filter(r => r.disposition === "AWAITING_APPROVAL").length} contacts are ready{outbound ? ` to ${stepName(outbound.stepType).toLowerCase()}` : ""}</b>
                <span>The run paused before acting so you can approve who goes in. Approve all, or pick who.</span>
              </div>
              <button className="ag-btn ag-btn--primary" onClick={() => history.push(`/agents/approvals?agent=${id}`)}><i className="material-icons-outlined">check_circle</i>Review &amp; approve</button>
            </div>
          )}
          {(run.status === "FAILED" || run.status === "CANCELLED") && run.error && (
            <div className="ag-live__why">{run.error}</div>
          )}

          <div className="ag-live__cols">
            <section className="ag-panel ag-live__steps" aria-label="Steps">
              <h2 className="ag-panel__title">Steps</h2>
              <ol className="ag-lsteps">
                {steps.map((n, i) => {
                  const s = summaryOf(n);
                  const now = live && !s && i === doneCount;
                  const kind = KIND[String(n.stepType).split(".")[0]] || "Step";
                  return (
                    <li key={n.nodeId || i} className={`ag-lstep${s ? " is-done" : now ? " is-now" : ""}`}>
                      <span className="ag-lstep__mark">{s ? "✓" : i + 1}</span>
                      <div className="ag-lstep__body">
                        <div className="ag-lstep__top"><span className="ag-lstep__kind">{kind}</span><span className="ag-lstep__name">{stepName(n.stepType)}</span></div>
                        <div className="ag-lstep__track"><span className={`ag-lstep__fill ${s ? "ag-prog--100"
                          : now && inStep && inStep.nodeId === n.nodeId ? progClass(inStep.done, inStep.total)
                          : now ? "is-grow" : "ag-prog--0"}`} /></div>
                        <span className="ag-lstep__note">
                          {s ? `${s.note ? plainNote(s.note) : `${s.processed} of ${s.entered} carried on`}${s.skipped ? ` · ${s.skipped} skipped` : ""}`
                            : now ? (inStep && inStep.nodeId === n.nodeId ? `${inStep.done} of ${inStep.total} done` : "Working on it now")
                            : live ? `Starts after step ${i}` : "Didn't run"}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ol>
              {live && !agent.trusted && outbound && !run.dryRun && (
                <p className="ag-hint">This agent’s first real run pauses before “{stepName(outbound.stepType)}” so you can approve who it acts on.</p>
              )}
            </section>

            <section className="ag-panel ag-live__people" aria-label="Contacts in this run">
              <div className="ag-live__phead">
                <h2 className="ag-panel__title">Contacts in this run</h2>
                <span className="ag-hint">newest first · click a row to see why</span>
                <span className="ag-live__spacer" />
                {FILTERS.map(f => (
                  <button key={f.key} className={`ag-fchip ag-fchip--sm${filter === f.key ? " is-on" : ""}`} onClick={() => setFilter(f.key)}>
                    {f.label}<span className="ag-fchip__n">{f.n}</span>
                  </button>
                ))}
              </div>
              {records.length === 0 ? (
                <div className="ag-hint ag-live__empty">
                  {live ? "Contacts show up here as each step finishes." : "No contacts in this run."}
                </div>
              ) : (
                <div className="ag-lrows">
                  <div className="ag-lrow ag-lrow--head"><span>Contact</span><span>Company</span><span>Outcome</span><span>Why</span></div>
                  {shown.map(r => {
                    const o = OUTCOME[r.disposition] || { label: r.disposition, cls: "" };
                    const trace = r.trace || [];
                    const open = openRow === (r.id || r.contactId);
                    return (
                      <React.Fragment key={r.id || r.contactId}>
                        <button className={`ag-lrow${open ? " is-open" : ""}`} onClick={() => setOpenRow(open ? null : (r.id || r.contactId))}>
                          <span className="ag-lrow__who"><span className="ag-lrow__av">{initials(r.name)}</span>{r.name || "Unnamed contact"}</span>
                          <span>{r.company || "—"}</span>
                          <span className={`ag-lrow__out ${o.cls}`}>{o.cls === "is-live" && <span className="ag-livedot" />}{o.label}</span>
                          <span className="ag-lrow__why">
                            {/* A test run doesn't look up emails, so "Would enroll" means "if an email is found". */}
                            {run.dryRun && !r.email && /^WOULD_/.test(r.disposition || "")
                              ? "If a verified email is found on the real run"
                              : r.reason || trace[trace.length - 1] || "—"}
                          </span>
                        </button>
                        {open && (
                          <div className="ag-lrow__trace">
                            <span className="ag-lrow__tracehead">How this contact was handled</span>
                            {trace.length ? trace.map((t, i) => <span key={i}>{t}</span>) : <span>No steps recorded yet.</span>}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
});

export default AgentRunLive;
